/**
 * P4 — provenance that travels WITH a fact.
 *
 * A document rarely speaks with one voice: a questionnaire is answered by a
 * named official, on a date, sometimes for a different project. When the
 * extractor lifts a figure out of such a document the report needs to know
 * who said it, in what role, when, and for which project — otherwise two
 * answers for "average daily flow" collapse into one number and the report
 * picks one silently.
 *
 * This module owns the attribution keys every extraction schema carries
 * (alongside the REF-LINEAGE triple), the prompt paragraph that asks for
 * them, the parser that lifts them off a payload, and the one formatter the
 * prompt renderers share. Pure; no I/O.
 */
import type { EvidenceAttribution } from '../../types/siteEvidence';

export const ATTRIBUTION_KEYS = [
  'statedBy',
  'statedByRole',
  'organisation',
  'statedOn',
  'appliesToProject',
  'projectName',
] as const;
export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];

const APPLIES = new Set<NonNullable<EvidenceAttribution['appliesToProject']>>(['this-site', 'other-project', 'unknown']);
const CAPS: Record<AttributionKey, number> = {
  statedBy: 120,
  statedByRole: 120,
  organisation: 120,
  statedOn: 40,
  appliesToProject: 20,
  projectName: 120,
};

/** Prompt paragraph shared by every document extraction. */
export const ATTRIBUTION_PROMPT =
  'For attribution, ONLY when the document states it (never guess): ' +
  'statedBy (the person or body making the statements), statedByRole (their title), ' +
  'organisation, statedOn (the date as written), projectName (the project the answers were prepared for), and ' +
  "appliesToProject: 'this-site' when the document addresses this site, 'other-project' when it says the answers " +
  "were prepared for a different project, otherwise omit it.";

/** Gemini response-schema properties for the attribution keys. */
export function attributionSchemaProps<T>(stringType: T): Record<AttributionKey, { type: T }> {
  return {
    statedBy: { type: stringType },
    statedByRole: { type: stringType },
    organisation: { type: stringType },
    statedOn: { type: stringType },
    appliesToProject: { type: stringType },
    projectName: { type: stringType },
  };
}

function cleanString(value: unknown, cap: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const clean = value.replace(/[\u0000-\u001f]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!clean || /^(?:null|none|n\/a|unknown|not stated)$/i.test(clean)) return undefined;
  return clean.slice(0, cap);
}

/**
 * Validate a raw attribution object. Returns undefined when nothing usable
 * was stated, so callers can spread `...(a ? { attribution: a } : {})`.
 */
export function normaliseAttribution(
  raw: Partial<Record<AttributionKey, unknown>> | null | undefined,
): EvidenceAttribution | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: EvidenceAttribution = {};
  const statedBy = cleanString(raw.statedBy, CAPS.statedBy);
  const statedByRole = cleanString(raw.statedByRole, CAPS.statedByRole);
  const organisation = cleanString(raw.organisation, CAPS.organisation);
  const statedOn = cleanString(raw.statedOn, CAPS.statedOn);
  const projectName = cleanString(raw.projectName, CAPS.projectName);
  const appliesRaw = cleanString(raw.appliesToProject, CAPS.appliesToProject)?.toLowerCase().replace(/[\s_]+/g, '-');
  if (statedBy) out.statedBy = statedBy;
  if (statedByRole) out.statedByRole = statedByRole;
  if (organisation) out.organisation = organisation;
  if (statedOn) out.statedOn = statedOn;
  if (projectName) out.projectName = projectName;
  if (appliesRaw && APPLIES.has(appliesRaw as NonNullable<EvidenceAttribution['appliesToProject']>) && appliesRaw !== 'unknown') {
    out.appliesToProject = appliesRaw as EvidenceAttribution['appliesToProject'];
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Read the attribution keys off a payload WITHOUT mutating it. */
export function readAttributionFromPayload(data: Record<string, unknown> | null | undefined): EvidenceAttribution | undefined {
  if (!data) return undefined;
  const raw: Partial<Record<AttributionKey, unknown>> = {};
  for (const key of ATTRIBUTION_KEYS) if (key in data) raw[key] = data[key];
  return normaliseAttribution(raw);
}

/**
 * Lift the attribution keys off an extraction payload so `slot.data` stays
 * the section's own shape (the same reason the lineage triple is projected
 * off — every consumer iterates `data` keys).
 */
export function takeAttributionFromPayload(data: Record<string, unknown>): EvidenceAttribution | undefined {
  const attribution = readAttributionFromPayload(data);
  for (const key of ATTRIBUTION_KEYS) delete data[key];
  return attribution;
}

/** Fill the gaps of `primary` from `secondary`; primary statements win. */
export function mergeAttribution(
  primary: EvidenceAttribution | undefined,
  secondary: EvidenceAttribution | undefined,
): EvidenceAttribution | undefined {
  if (!primary) return secondary;
  if (!secondary) return primary;
  const out: EvidenceAttribution = { ...primary };
  for (const key of ATTRIBUTION_KEYS) {
    if (out[key] === undefined && secondary[key] !== undefined) (out as Record<string, unknown>)[key] = secondary[key];
  }
  // Multiple windows can contribute to one typed payload. A head-window
  // this-site label must not relabel later other-project figures as local.
  if (primary.appliesToProject === 'other-project' || secondary.appliesToProject === 'other-project') {
    out.appliesToProject = 'other-project';
    out.projectName = (primary.appliesToProject === 'other-project' ? primary : secondary).projectName;
  }
  return out;
}

/** Content-index segments store nullable strings; convert to the SED shape. */
export function attributionFromSegment(
  segment: { statedBy?: string | null; statedByRole?: string | null; organisation?: string | null; statedOn?: string | null; appliesToProject?: string | null } | undefined,
): EvidenceAttribution | undefined {
  if (!segment) return undefined;
  return normaliseAttribution({
    statedBy: segment.statedBy ?? undefined,
    statedByRole: segment.statedByRole ?? undefined,
    organisation: segment.organisation ?? undefined,
    statedOn: segment.statedOn ?? undefined,
    appliesToProject: segment.appliesToProject ?? undefined,
  });
}

export function attributionIsForAnotherProject(a: EvidenceAttribution | undefined): boolean {
  return a?.appliesToProject === 'other-project';
}

/**
 * One line for prompts and panels, e.g.
 * `stated by J. Smith (Director of Water), City of DeRidder, on 3 Sep 2026 — PREPARED FOR ANOTHER PROJECT (Project Alpha)`.
 * Empty string when nothing was stated.
 */
export function formatAttribution(a: EvidenceAttribution | undefined): string {
  if (!a) return '';
  const parts: string[] = [];
  if (a.statedBy) parts.push(`stated by ${a.statedBy}${a.statedByRole ? ` (${a.statedByRole})` : ''}`);
  else if (a.statedByRole) parts.push(`stated by the ${a.statedByRole}`);
  if (a.organisation) parts.push(a.organisation);
  if (a.statedOn) parts.push(`on ${a.statedOn}`);
  let text = parts.join(', ');
  if (a.appliesToProject === 'other-project') {
    text += `${text ? ' — ' : ''}PREPARED FOR ANOTHER PROJECT${a.projectName ? ` (${a.projectName})` : ''}`;
  } else if (a.projectName) {
    text += `${text ? ', ' : ''}for ${a.projectName}`;
  }
  return text;
}
